import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../auth/AuthContext";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const referralCode = params.get("ref") || "";
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/auth/magic-link", {
        email,
        referral_code: referralCode || undefined,
        frontend_url: `${window.location.origin}/auth/verify`,
      });
      setStatus("sent");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send the sign-in link. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#08080A] px-5 py-16">
      <div className="pointer-events-none absolute inset-0 gold-glow-top" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-grid-faint [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-xs text-[#636B78] transition-colors hover:text-white" data-testid="login-back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to website
        </Link>

        <div className="rounded-3xl border border-white/10 bg-[#14141A]/90 p-8 shadow-[0_20px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-10" data-testid="login-card">
          <div className="flex flex-col items-center text-center">
            <img src="/winter-logo.jpg" alt="Winter Protocol" className="h-14 w-14 rounded-2xl object-cover mix-blend-screen" />
            <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-white">
              Welcome to Winter Protocol
            </h1>
            <p className="mt-2 text-sm text-[#99A1AF]">Sign in to your members area</p>
          </div>

          {status === "sent" ? (
            <div className="mt-8 text-center" data-testid="login-success-message">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <Mail className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <p className="mt-4 font-display text-lg font-semibold text-white">Check your email</p>
              <p className="mt-2 text-sm leading-relaxed text-[#99A1AF]">
                We sent a secure sign-in link to <span className="text-white">{email}</span>.
                The link expires in 20 minutes and can be used once.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="mt-6 text-xs text-[#636B78] underline-offset-4 hover:text-white hover:underline"
                data-testid="login-use-different-email"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={googleLogin}
                data-testid="login-google-button"
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#08080A] transition-transform duration-300 hover:scale-[1.02]"
              >
                <GoogleMark />
                Continue with Google
              </button>

              <div className="my-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#636B78]">OR</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              <form onSubmit={sendMagicLink}>
                <label htmlFor="login-email" className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                  className="mt-2 w-full rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3.5 text-sm text-white placeholder:text-[#3A3F4A] outline-none transition-colors focus:border-[#D4AF37]/60"
                />
                {referralCode && (
                  <p className="mt-3 font-mono text-[11px] text-[#D4AF37]" data-testid="login-referral-applied">
                    Referral code applied: {referralCode.toUpperCase()}
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-3 text-sm text-red-400" data-testid="login-error-message">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="login-send-magic-link-button"
                  className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#08080A] transition-all duration-300 hover:bg-[#F3E5AB] disabled:opacity-60"
                >
                  {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
                  {status === "sending" ? "Sending…" : "Send Magic Link"}
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-[11px] leading-relaxed text-[#3A3F4A]">
            Passwordless sign-in. One account across the Winter Protocol app and website.
          </p>
        </div>
      </div>
    </div>
  );
}
